import { array, arrayOf, shape, func, string, objectOf } from 'prop-types';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import ArrowDownIcon from 'ravenjs/lib/SvgIcon/types/ArrowDownIcon';
import { callFunc, getHandler } from 'ravenjs/utils/actions';
import { removeEventListenerOnRoot, addEventListenerOnRoot } from 'ravenjs/utils/events';
import { get } from 'ravenjs/utils/lodash';
import { getThemeProps } from 'ravenjs/utils/theme';
import Col from 'ravenjs/lib/Col';

import { IconTypes } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import { formatIconColors } from 'utils/dataList';

const ActionButton = styled.div`
    align-items: center;
    color: ${getThemeProps('palette.secondary.text')};
    cursor: pointer;
    display: flex;
    height: 36px;
    border: solid 1px ${getThemeProps('palette.secondary.borderColor')};
    background: ${getThemeProps('palette.secondary.gradientColor')};
    background: ${({ backgroundColor }) =>
        backgroundColor || getThemeProps('palette.secondary.color')};
    min-width: ${({ minWidth }) => minWidth || '125px'};
    justify-content: center;
    &:hover {
        background: ${({ hoverColor }) => hoverColor || getThemeProps('palette.secondary.color')};
    }
    &:focus {
        box-shadow: none !important;
    }
    > svg {
        margin-right: 5px;
    }
`;

const ActionItems = styled.div`
    ${({ theme }) => theme.media.up('md')`
        position: absolute;
    `};
    cursor: pointer;
    border-radius: 0;
    border: solid 1px ${DEFAULT_COLORS.DARK};
    background: ${DEFAULT_COLORS.COMPONENTS_THE_GREY_STYLE};
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.16);
    ${({ dropdownPosition }) => dropdownPosition}: 0;
    top: 36px;
    min-width: 240px;
    &:hover {
        background: ${DEFAULT_COLORS.COMPONENTS_TABLE_TITLE_ROW};
    }
    z-index: 12;
`;

const ActionItemStyled = styled(Col)`
    white-space: nowrap;
    display: flex;
    border-bottom: solid 1px ${DEFAULT_COLORS.LIGHTLY};
    align-items: center;
    justify-content: start;
    height: 38px;
    padding: ${({ dropdownPosition }) => (dropdownPosition === 'left' ? '1rem' : 0)};
    ${({ isDisable }) =>
        isDisable &&
        `
        pointer-events: none;
        > p {
            color: ${DEFAULT_COLORS.GREY_2};
        }
        > svg {
            opacity: 0.5;
        }
    `}
`;

const ButtonLabelStyled = styled.span`
    font-family: Favorit;
    font-size: 0.813rem;
    line-height: normal;
`;

const TitleStyled = styled.p`
    margin: 0;
    align-self: center;
    line-height: normal;
    color: ${DEFAULT_COLORS.BLACK};
    font-family: Roboto;
    font-size: 0.875rem;
    font-weight: 300;
    &:hover {
        font-weight: 500;
    }
    ${({ icon }) => !icon && `margin-left: 25px;`}
`;

const DropdownActionsWrapper = styled.div`
    position: relative;
`;

const DropdownActions = ({
    label,
    handlers,
    items,
    DropdownIcon,
    styles,
    dropdownPosition = 'right',
}) => {
    const dropdownWrapperRef = useRef(null);
    const [open, setOpen] = React.useState(false);
    const onClickAction = () => {
        const isOpen = !open;
        setOpen(isOpen);
    };

    const handleOnClick = event => {
        if (dropdownWrapperRef.current && !dropdownWrapperRef.current.contains(event.target)) {
            setOpen(false);
        }
    };

    const handleOnActionItemClick = onClickHandler => {
        return () => {
            setOpen(false);
            callFunc(onClickHandler);
        };
    };

    useEffect(() => {
        addEventListenerOnRoot('click', handleOnClick);
        return () => {
            removeEventListenerOnRoot('click', handleOnClick);
        };
    });

    return (
        <DropdownActionsWrapper ref={dropdownWrapperRef}>
            <ActionButton {...styles} onClick={onClickAction}>
                {DropdownIcon ? <DropdownIcon /> : <ArrowDownIcon stroke="currentColor" />}
                {!DropdownIcon && <ButtonLabelStyled>{label.toUpperCase()}</ButtonLabelStyled>}
            </ActionButton>
            {open && (
                <ActionItems dropdownPosition={dropdownPosition}>
                    {items.map(item => {
                        const { label, icon, iconProps, onClickRef } = item;
                        const Icon = IconTypes[icon];
                        const onClickHandler = getHandler(handlers, onClickRef);
                        const isDisable = get(item, 'btnProps.isDisable', false);
                        return (
                            <ActionItemStyled
                                isDisable={isDisable}
                                key={label}
                                onClick={handleOnActionItemClick(onClickHandler)}
                                dropdownPosition={dropdownPosition}
                            >
                                {icon && (
                                    <Icon
                                        style={{ margin: '0 0.32rem' }}
                                        {...formatIconColors(iconProps)}
                                    />
                                )}
                                <TitleStyled icon={icon}>{label}</TitleStyled>
                            </ActionItemStyled>
                        );
                    })}
                </ActionItems>
            )}
        </DropdownActionsWrapper>
    );
};
DropdownActions.propTypes = {
    handlers: objectOf(func),
    label: string,
    dropdownPosition: string,
    items: arrayOf(
        shape({
            allowedStatus: array,
            onClick: func,
            title: string,
        })
    ),
    DropdownIcon: func,
    styles: shape({
        minWidth: string,
    }),
};

DropdownActions.defaultProps = {
    handlers: {},
    label: 'Actions',
    dropdownPosition: 'right',
    items: [],
    DropdownIcon: null,
    styles: {},
};
export default DropdownActions;
